import React, { useState, useEffect, useRef, useMemo, startTransition } from 'react';

interface FeatureListProps {
  features: string[];
  unavailable?: string[];
  boldCount?: number; // Number of features to make bold from the start
}

const FeatureList: React.FC<FeatureListProps> = ({ features, unavailable = [], boldCount = 0 }) => {
  const scrollRef = useRef<HTMLUListElement>(null);
  const [showScroll, setShowScroll] = useState(false);

  // Ensure unavailable is always an array
  const unavailableList = useMemo(
    () => (Array.isArray(unavailable) ? unavailable : []),
    [unavailable]
  );

  useEffect(() => {
    const checkOverflow = () => {
      if (scrollRef.current) {
        const { scrollHeight, clientHeight } = scrollRef.current;
        startTransition(() => setShowScroll(scrollHeight > clientHeight));
      }
    };

    checkOverflow();
    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, [features, unavailableList]);

  const handleScroll = () => {
    if (scrollRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
      if (scrollTop + clientHeight >= scrollHeight - 10) {
        setShowScroll(false);
      } else {
        setShowScroll(true);
      }
    }
  };

  return (
    <>
      <ul className='featureList' ref={scrollRef} onScroll={handleScroll}>
        {features &&
          features?.map((feature, index) => (
            <li key={index} className={`featureItem ${index < boldCount ? 'bold' : ''}`}>
              <span className='featureIcon available'>✔</span>
              <span className='featureText'>{feature}</span>
            </li>
          ))}
        {unavailableList &&
          unavailableList.map((feature, index) => (
            <li key={`unavailable-${index}`} className='featureItem unavailable'>
              <span className='featureIcon unavailable'>✗</span>
              <span className='featureText'>{feature}</span>
            </li>
          ))}
      </ul>
      {showScroll && <div className='scrollIndicator'></div>}
    </>
  );
};

export default FeatureList;
